/* ==========================================================================
   Sticky add-to-cart - fast path.

   WHY THIS EXISTS
   The sticky bar's own handler inside sections/gwb-product-main.liquid does
   three things in sequence before the customer sees anything happen:
       POST /cart/add.js  ->  GET /cart.js  ->  then play the fly-to-cart
   That is two network round trips before the animation starts, which is why
   the floating button feels slower than the button in the hero. The hero uses
   Horizon's native product form, which animates immediately and treats the
   network as a background task.

   This module gives the sticky button the same feel:
     - the fly-to-cart animation fires INSTANTLY on tap, before any request
     - the cart event is dispatched straight away so the drawer opens at once
     - the single follow-up fetch resolves the event's promise in the
       background, which is what refreshes the drawer contents and the count

   It claims the click in the CAPTURE phase and calls stopImmediatePropagation,
   so the section's slower handler never runs and the animation cannot play
   twice. If this module fails to load for any reason, the section's original
   handler still works - the behaviour simply reverts to the slower path.
   ========================================================================== */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

(function () {
  function init() {
    var addBtn = document.getElementById('GwbStickyAdd');
    var nativeBuy = document.getElementById('GwbNativeBuy');
    if (!addBtn || !nativeBuy || addBtn.dataset.gwbFast === '1') return;
    addBtn.dataset.gwbFast = '1';

    function currentVariantId() {
      var form = document.querySelector('form[data-type="add-to-cart-form"]');
      var input = form ? form.querySelector('input[name="id"]') : null;
      return input ? input.value : null;
    }

    function flyToCart(source) {
      var dest = document.querySelector('cart-icon');
      if (!window.customElements || !customElements.get('fly-to-cart') || !dest) return;
      var img = document.querySelector('#GwbGalWrap img');
      var fly = document.createElement('fly-to-cart');
      fly.className = 'fly-to-cart--quick';
      if (img) fly.style.backgroundImage = 'url(' + img.src + ')';
      fly.useSourceSize = true;
      fly.source = source;
      fly.destination = dest;
      document.body.appendChild(fly);
    }

    addBtn.addEventListener('click', function (event) {
      // Take ownership before the section's slower handler sees the click.
      event.preventDefault();
      event.stopImmediatePropagation();

      if (addBtn.dataset.busy) return;
      var variantId = currentVariantId();
      if (!variantId) return;

      var qtyEl = document.getElementById('GwbStickyQtyVal');
      var qty = parseInt(qtyEl && qtyEl.textContent, 10) || 1;

      addBtn.dataset.busy = '1';
      var original = addBtn.textContent;
      addBtn.textContent = 'Added \u2713';

      // 1. Animate first. Nothing here waits on the network.
      flyToCart(addBtn);

      // 2. Tell the theme immediately so the drawer opens without delay.
      var deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: qty }],
        promise: deferred.promise
      }));

      // 3. Do the work in the background and settle the promise, which is what
      //    populates the drawer and updates the count.
      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) {
            deferred.reject(new Error(res.message || 'Add to cart failed'));
            document.dispatchEvent(new CartErrorEvent({
              error: res.description || res.message || 'Add to cart failed',
              code: 'INVALID'
            }));
            addBtn.textContent = original;
            delete addBtn.dataset.busy;
            return;
          }
          return fetch(Theme.routes.cart_url + '.json', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { items: cart.items, source: 'gwb-sticky-atc', itemCount: qty, didError: false }
              });
            });
        })
        .catch(function (err) {
          deferred.reject(err);
          console.warn('[gwb-sticky-add] failed', err);
          addBtn.textContent = original;
          delete addBtn.dataset.busy;
        })
        .finally(function () {
          setTimeout(function () {
            addBtn.textContent = original;
            delete addBtn.dataset.busy;
          }, 1200);
        });
    }, true); // capture
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
